import React, { useState } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { ConvertFeetToMeters, DateHasAlreadyPassed, GetTimezone, YEAR_IN_MINUTES } from "../../util";

import { useUserAuth } from "../../contexts/authContext";

export const ConstraintDraw = (props) => {
    const ADVISORY_TYPE = "ADVISORY";
    const TRAINING_TYPE = "TRAINING";
    const COMMERCIAL_TYPE = "COMMERCIAL";
    const PUBLIC_SAFETY_TYPE = "PUBLIC_SAFETY";
    const EMERGENCY_TYPE = "EMERGENCY";
    const RESTRICTED_TYPE = "RESTRICTED";

    const [createName, setCreateName] = useState("");
    const [createStart, setCreateStart] = useState(dayjs().add("10", "minutes").set("seconds", 0));
    const [createEnd, setCreateEnd] = useState(dayjs().add("70", "minutes").set("seconds", 0));

    const [createAltitude, setCreateAltitude] = useState(0);
    const [createDuration, setCreateDuration] = useState(60);

    const [createType, setCreateType] = useState(ADVISORY_TYPE);
    const [createPriority, setCreatePriority] = useState(0);

    const { user, handleFailedFetch } = useUserAuth();

    const handleTypeChange = (e) => {
        const type = e.target.value;
        setCreateType(type);

        if (type === RESTRICTED_TYPE) {
            setCreatePriority(1000);
        } else if (type === EMERGENCY_TYPE) {
            setCreatePriority(40);
        } else if (type === PUBLIC_SAFETY_TYPE) {
            setCreatePriority(30);
        } else if (type === COMMERCIAL_TYPE) {
            setCreatePriority(20);
        } else if (type === TRAINING_TYPE) {
            setCreatePriority(10);
        } else if (type === ADVISORY_TYPE) {
            setCreatePriority(0);
        }
    };
    const handleStartDateChange = (startDate) => {
        setCreateStart(startDate);

        const durationMin = parseFloat(createDuration);
        const validDuration = !isNaN(durationMin) && durationMin > 0;
        if (dayjs(startDate).isValid() && validDuration) {
            setCreateEnd(dayjs(startDate).add(durationMin, "minutes"));
        }
    };
    const handleDurationChange = (e) => {
        const durationMin = e.target.value;
        setCreateDuration(durationMin);

        const validDuration = !isNaN(durationMin) && durationMin > 0;
        if (validDuration && dayjs(createStart).isValid()) {
            setCreateEnd(dayjs(createStart).add(durationMin, "minutes"));
        }
    };
    const handlePublish = async () => {
        const invalidName = !createName;
        const invalidStartDate = dayjs().isValid() === false || DateHasAlreadyPassed(createStart);
        const invalidAltitude = isNaN(parseFloat(createAltitude)) || parseFloat(createAltitude) <= 0;
        const invalidDuration = isNaN(parseFloat(createDuration)) || parseFloat(createDuration) <= 0 || parseFloat(createDuration) >= YEAR_IN_MINUTES;
        if (invalidName || invalidStartDate || invalidAltitude || invalidDuration) {
            props.setSnackbar({ children: "Please fill out the required fields.", severity: "error" });
            return false;
        }

        const constraint = {
            name: createName,
            type: createType,
            altitude: ConvertFeetToMeters(parseFloat(createAltitude)),
            organization: user.organization,
            organization_id: user.organization_id,
            created_user_id: user.id,
            priority: createPriority,
            time_start: createStart,
            time_end: createEnd,
            volumes: props.constraintCreateVertices,
            state: "PLANNING",
            version: 1
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        await fetch("api/constraint/planning", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.setSnackbar({ children: "Constraint successfully created", severity: "success" });
                props.setConstraintCreateDialogOpen(false);
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleCancel = () => {
        props.setConstraintCreateDialogOpen(false);
    };
    return (
        <Dialog
            open={props.constraintCreateDialogOpen}
            onContextMenu={(e) => e.preventDefault()}
            fullWidth={true}
            maxWidth={"sm"}
            container={props.mapContainer ? props.mapContainer : undefined}
        >
            <DialogTitle>Create Constraint</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2, pt: 1 }}>
                    <TextField
                        inputProps={{ "data-testid": "createName" }}
                        onChange={(e) => setCreateName(e.target.value)}
                        error={props.snackbar && !createName}
                        variant="outlined"
                        id="nameInput"
                        label="Name"
                        value={createName}
                        sx={{ flex: 1 }}
                    />

                    <TextField
                        inputProps={{ "data-testid": "inputAltitude" }}
                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        onChange={(e) => setCreateAltitude(e.target.value)}
                        error={props.snackbar && (isNaN(parseFloat(createAltitude)) || parseFloat(createAltitude) <= 0)}
                        variant="outlined"
                        id="createAltitude"
                        label="Altitude (AGL)"
                        value={createAltitude}
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        sx={{ flex: 1 }}
                    />

                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel id="select-priority-air">Type</InputLabel>
                        <Select
                            value={createType}
                            id="selectType"
                            labelId="select-label"
                            label="Type"
                            onChange={handleTypeChange}
                            MenuProps={{ container: props.mapContainer }}
                        >
                            <MenuItem value={ADVISORY_TYPE}>Advisory</MenuItem>
                            <MenuItem value={TRAINING_TYPE}>Training</MenuItem>
                            <MenuItem value={COMMERCIAL_TYPE}>Commercial</MenuItem>
                            <MenuItem value={PUBLIC_SAFETY_TYPE}>Public Safety</MenuItem>
                            <MenuItem value={EMERGENCY_TYPE}>Emergency</MenuItem>
                            <MenuItem value={RESTRICTED_TYPE}>Restricted</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label={`Start Time (${GetTimezone()}) *`}
                                value={createStart}
                                onChange={handleStartDateChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: props.snackbar && DateHasAlreadyPassed(createStart),
                                        inputProps: { "data-testid": "start-date-picker" }
                                    },
                                    popper: {
                                        container: props.mapContainer
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Box>

                    <TextField
                        error={
                            props.snackbar &&
                            (isNaN(parseFloat(createDuration)) || parseFloat(createDuration) <= 0 || parseFloat(createDuration) >= YEAR_IN_MINUTES)
                        }
                        InputProps={{ endAdornment: <InputAdornment position="end">mins</InputAdornment> }}
                        variant="outlined"
                        id="durationInput"
                        label="Duration"
                        onChange={handleDurationChange}
                        value={createDuration}
                        sx={{ flex: 1 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} size="small">
                    Cancel
                </Button>
                <Button onClick={handlePublish} data-testid="accept" size="small">
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};
